import { useMemo, useState } from "react";
import { Field, getIn } from "formik";
import { useTranslation } from "react-i18next";
import _ from "lodash";
import {
  Grid,
  Form,
  Message,
  Radio,
  Button,
  Label,
  Icon,
  Input,
  Dropdown,
} from "semantic-ui-react";
import SemanticDatepicker from "react-semantic-ui-datepickers";
import "react-semantic-ui-datepickers/dist/react-semantic-ui-datepickers.css";
import { getLanguageOptions } from "services/common";

export const ErrorText = ({ name }) => (
  <Field name={name}>
    {({ field, form, meta }) => {
      const error = getIn(form.errors, name);
      const touch = getIn(form.touched, name);
      return typeof error === "string" && (touch || form.submitCount) ? (
        <p className="form-error-text">{error}</p>
      ) : null;
    }}
  </Field>
);

const errorObjToList = (errors, t, tPrefix) => {
  if (_.isEmpty(errors)) return [];

  console.error("errorObjToList", errors);
  const list = [];

  Object.keys(errors).forEach((key) => {
    if (typeof errors[key] === "string")
      list.push(
        <li key={key}>
          <b>{t(tPrefix + key)}</b>: <i>{errors[key]}</i>
        </li>
      );
    else if (Array.isArray(errors[key]))
      list.push(
        <li key={key}>
          <b>{t(tPrefix + key)}</b>:
          {errors[key].map(
            (error, i) =>
              error && (
                <li key={key + i}>
                  <i>{error}</i>
                </li>
              )
          )}
        </li>
      );
  });

  return list;
};

export const ErrorInfo = ({ errors, submitCount, t, tPrefix }) => {
  const errorList = useMemo(() => errorObjToList(errors, t, tPrefix), [submitCount]);
  if (!errorList.length) return null;
  return <Message error list={errorList} />;
};

const getFieldRowComponent = (props) => {
  const { type, options } = props;

  if (options || type === "select") return Form.Select;
  if (type === "date") return SemanticDatepicker;
  if (type === "area") return Form.TextArea;
  if (type === "radio") return Radio;
  if (type === "weekdays") return WeekDaySelect;
  return Form.Input;
};

export const FieldRow = (props) => {
  const { name, label, onChange: _onChange, valueAccessor, children, ...otherProps } = props;
  const Component = getFieldRowComponent(props);

  return (
    <Field name={name}>
      {({
        field: { value, onChange }, // { name, value, onChange, onBlur }
        form: { touched, errors }, // also values, setXXXX, handleXXXX, dirty, isValid, status, etc.
        meta,
      }) => (
        <Grid.Row className="form-field-row">
          <Grid.Column width={3} textAlign="right">
            <label htmlFor={name}>{label}</label>
          </Grid.Column>
          <Grid.Column width={13}>
            <Component
              style={Component === Form.TextArea ? { whiteSpace: "pre-wrap" } : {}}
              {...otherProps}
              id={name}
              name={name}
              value={valueAccessor ? valueAccessor(value) : value}
              error={errors[name] !== undefined}
              onChange={_onChange || onChange}
              fluid={Component !== Form.TextArea ? true : null} // "fluid" is not a prop for TextAreas
            />
            {children}
          </Grid.Column>
        </Grid.Row>
      )}
    </Field>
  );
};

export const FieldInput = (props) => {
  const { name, type, value: _value, onChange: _onChange, ...otherProps } = props;
  const Component = getFieldRowComponent(props);
  return (
    <Field name={name}>
      {({
        field: { value, onChange }, // { name, value, onChange, onBlur }
        form: { touched, errors }, // also values, setXXXX, handleXXXX, dirty, isValid, status, etc.
        meta,
      }) => (
        <Component
          {...otherProps}
          id={name}
          name={name}
          error={errors[name] !== undefined}
          onChange={_onChange || onChange}
          // inline
          fluid
          value={value}
        />
      )}
    </Field>
  );
};

const WeekDaySelect = (props) => {
  const { t } = useTranslation("common");
  const WEEKDAYS = [
    t("playday_monday"),
    t("playday_tuesday"),
    t("playday_wednesday"),
    t("playday_thursday"),
    t("playday_friday"),
    t("playday_saturday"),
    t("playday_sunday"),
  ];
  // eslint-disable-next-line no-unused-vars
  const { name, type, onChange, ...otherProps } = props;
  const value = props.value || "1111111";

  const toggleDay = (index) => {
    const newChar = value[index] === "0" ? "1" : "0";
    const newValue = value.substring(0, index) + newChar + value.substring(index + 1);
    onChange(name, newValue);
  };

  return (
    <Button.Group>
      {WEEKDAYS.map((day, i) => (
        <Button
          type="button"
          key={day}
          toggle
          compact
          size="mini"
          active={value[i] !== "0"}
          onClick={() => toggleDay(i)}
        >
          {day}
        </Button>
      ))}
    </Button.Group>
  );
};

export const MultiSelectTagInputField = ({ tags = [], name, label, onChange, value = [] }) => {
  const { t } = useTranslation("common");
  const [customInput, setCustomInput] = useState("");

  const selectedValues = value;
  const availableTags = tags.filter((tag) => !selectedValues.includes(tag));

  const handleSelect = (val) => {
    const updated = [...selectedValues, val];
    onChange?.(updated);
  };

  const handleRemove = (valToRemove) => {
    const updated = selectedValues.filter((val) => val !== valToRemove);
    onChange?.(updated);
  };

  const handleCustomSubmit = (e) => {
    e.preventDefault();
    const val = parseInt(customInput.trim(), 10);
    if (!isNaN(val) && !selectedValues.includes(val)) {
      const updated = [...selectedValues, val];
      onChange?.(updated);
    }
    setCustomInput("");
  };

  const handleInputChange = (e) => {
    const val = e.target.value;
    if (/^\d*$/.test(val)) {
      setCustomInput(val);
    }
  };

  return (
    <Grid.Row className="form-field-row">
      <Grid.Column width={3} textAlign="right">
        <label htmlFor={name}>{label}</label>
      </Grid.Column>
      <Grid.Column width={13}>
        <div className="selected-tags-field">
          {selectedValues.length > 0 ? (
            selectedValues.map((val) => (
              <Label color="green" key={`selected-${val}`} className="selected-tags">
                {val}
                <Icon name="delete" onClick={() => handleRemove(val)} />
              </Label>
            ))
          ) : (
            <p>{t("select_values")}</p>
          )}
        </div>

        <div className="tag-options">
          {availableTags.map((val) => (
            <Label color="green" basic onClick={() => handleSelect(val)} key={`option-${val}`}>
              {val}
            </Label>
          ))}
          <Input
            style={{ marginLeft: "4px" }}
            size="mini"
            placeholder={t("other")}
            value={customInput}
            onChange={handleInputChange}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                handleCustomSubmit(e);
              }
            }}
            inputMode="numeric"
          />
        </div>
      </Grid.Column>
    </Grid.Row>
  );
};

export const MultiSelectLanguageField = ({ name = "languages", label, onChange, value = [] }) => {
  return (
    <Grid.Row className="form-field-row">
      <Grid.Column width={3} textAlign="right">
        <label htmlFor={name}>{label}</label>
      </Grid.Column>

      <Grid.Column width={13}>
        <Dropdown
          id={name}
          options={getLanguageOptions()}
          multiple
          search
          selection
          value={value}
          onChange={(_, data) => onChange(data.value)}
        />
      </Grid.Column>
    </Grid.Row>
  );
};
